import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MaterialIconToolTip from '../../../../common/MaterialIconToolTip';
import { SkeltonCardTwo } from '../../../../common/SkeltonCardTwo';
import { getFileType, getLookupWitHDefaultValue } from '../../../../utils/common';
import { openCustomModal, openMediaViewModal } from '../../../../actions/modal';
import EditCustomerReport from '../../Modals/EditCustomerReport';
import { chatType } from '../../ChatController';
import { getChatMedia } from '../../../../actions/chat';
import { getMediaType } from './GroupMedia';
import useHeicImage from '../../../../hooks/useHeicImage';

const mediaFileType = getLookupWitHDefaultValue({
    lookup: {
        audio: '/img/audio.png',
        video: '/img/video.png',
        pdf: '/img/pdf.png',
        file: '/img/file.png',
        others: '/img/unknownFileType.png',
        xls: '/img/xls.png',
        text: '/img/txt-file.png',
    },
    defaultValue: '/img/unknownFileType.png',
});

function CoversationMedia({ mainDivClass, setShowMedia, showCustomerReport }) {
    const { openedConversationId } = useSelector((state) => state.conversations);
    const { isSidebarMediaLoading } = useSelector((state) => state.loading);
    const dispatch = useDispatch();
    const chatMedia = useSelector((state) => state.chat.chatMedia);
    const chatDetails = useSelector((state) => state.chat.chatDetails);

    const isTicketChat = chatDetails[openedConversationId]?.type === chatType.TICKET;

    const media = useMemo(() => {
        if (chatMedia[`${openedConversationId}-ALL`]) {
            return chatMedia[`${openedConversationId}-ALL`];
        }
        return [];
    }, [chatMedia, openedConversationId]);

    useEffect(() => {
        dispatch(getChatMedia({ id: openedConversationId }));
    }, [dispatch, openedConversationId]);

    const openCustomerReport = () => {
        return dispatch(
            openCustomModal({
                heading: `Edit Customer Report`,
                modalWidth: '60vw',
                modalHeight: 'auto',
                modalComponent: <EditCustomerReport openedConversationId={openedConversationId} ticketId={chatDetails.id} />,
            })
        );
    };



    const getMediaToShow = () => {
        if (isSidebarMediaLoading) {
            return <SkeltonCardTwo arr={[1, 2, 3]} height={13} />;
        }

        if (Array.isArray(media) && media.length > 0) {
            return (
                <div className="grid grid-cols-3 gap-3 mt-3">
                    {media?.slice(0, 6).map((item, index) => (
                        <MediaItem item={item} index={index} key={item.id} isTicketChat={isTicketChat} media={media} />
                    ))}
                </div>
            );
        }

        return (
            <div className="py-5 text-center text-gray-500">
                <p className="text-base">No media available</p>
            </div>
        );
    };

    return (
        <div className={mainDivClass}>
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-scogoprimary flex items-center">
                    <span className="material-icons mr-2 text-scogoprimary text-font16">perm_media</span>
                    Media <span className="ml-2 text-base font-medium text-gray-500">{media?.length || 0}</span>
                </h3>

                <div className="flex items-center">
                    {showCustomerReport && (
                        <MaterialIconToolTip
                            onClick={openCustomerReport}
                            title='Edit Customer Report'
                            materialIconName='description'
                            materialIconClass='cursor-pointer text-scogoprimary material-icons hover:text-scogoorange text-font16 mr-3'
                        />
                    )}
                    <button
                        onClick={() => setShowMedia(true)}
                        className="flex items-center text-sm font-medium text-scogoprimary hover:text-scogoorange transition-colors duration-200"
                    >
                        <span className="material-icons text-font16">navigate_next</span>
                    </button>
                </div>
            </div>

            {getMediaToShow()}
        </div>
    );
}

export default CoversationMedia;



const MediaItem = ({ item, index, isTicketChat, media }) => {
    const dispatch = useDispatch();
    const isDuplicate = item.is_duplicate;

    return (
        <div className="relative group" key={item.id}>
            <div
                className="w-24 h-24 rounded-lg overflow-hidden border-2 border-transparent hover:border-scogoorange transition-all duration-300 cursor-pointer hover:shadow-lg hover:scale-105"
                onClick={() => {
                    dispatch(openMediaViewModal({ attachments: media, openedMediaIndex: index, isTicketChat }));
                }}
            >
                {getFileType(item.mime_type) === 'image' ? (
                    <ThumbnailWithHeic url={item.thumbnail_url} mimeType={item.mime_type} />
                ) : (
                    <img
                        src={process.env.PUBLIC_URL + `${mediaFileType[item.type]}`}
                        className="max-w-max h-full object-cover"
                        alt={item.name || 'Media'}
                    />
                )}
            </div>
            {isDuplicate && (
                <div className="absolute top-0 right-0 left-0 max-w-max bg-red-500 text-white text-xs px-1 py-0.5 rounded-bl-md">
                    Duplicate
                </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1.5 opacity-0 group-hover:opacity-100 transition-opacity truncate w-24">
                {item.name}
            </div>
        </div>
    );
};

const ThumbnailWithHeic = ({ url, mimeType }) => {
    const { src: renderSrc, isConverting } = useHeicImage(url, mimeType);

    return (
        <div className="relative h-full w-full">
            <img
                src={renderSrc}
                className="max-w-max h-full object-cover"
                alt=""
            />
            {isConverting && (
                <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 text-white text-xs'>
                    <span className='material-icons text-sm'>hourglass_empty</span>
                </div>
            )}
        </div>
    );
};